/**
 * Select stylé type menu animé (inspiré Uiverse) — sync avec un <select> natif.
 */

const CHEVRON_PATH =
  'M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393 c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393 s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z';

export interface MenuSelectHoverPayload {
  value: string | null;
  previewHost: HTMLElement;
}

export interface EnhanceMenuSelectOptions {
  /** Survol d’une option (null = quitte la liste / ferme). */
  onOptionHover?: (payload: MenuSelectHoverPayload) => void;
}

function createChevron(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'menu-select__chevron');
  svg.setAttribute('viewBox', '0 0 360 360');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', CHEVRON_PATH);
  svg.appendChild(path);
  return svg;
}

function selectedLabel(select: HTMLSelectElement): string {
  const opt = select.selectedOptions[0];
  return opt?.textContent?.trim() || '—';
}

/** Enveloppe un select natif dans un menu déroulant animé. */
export function enhanceMenuSelect(
  select: HTMLSelectElement,
  options: EnhanceMenuSelectOptions = {}
): HTMLDivElement {
  if (select.closest('.menu-select')) {
    return select.closest('.menu-select') as HTMLDivElement;
  }

  select.classList.add('menu-select-native');
  select.setAttribute('tabindex', '-1');
  select.setAttribute('aria-hidden', 'true');

  const root = document.createElement('div');
  root.className = 'menu-select';

  const item = document.createElement('div');
  item.className = 'menu-select__item';

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'menu-select__trigger';
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  if (select.id) {
    trigger.id = `${select.id}-trigger`;
    const label = document.querySelector(`label[for="${select.id}"]`);
    if (label) label.setAttribute('for', trigger.id);
  }

  const labelEl = document.createElement('span');
  labelEl.className = 'menu-select__label';
  labelEl.textContent = selectedLabel(select);

  trigger.append(labelEl, createChevron());

  const hoverPreview = document.createElement('div');
  hoverPreview.className = 'menu-select__hover-preview';
  hoverPreview.setAttribute('aria-hidden', 'true');
  const previewHost = document.createElement('div');
  previewHost.className = 'menu-select__hover-preview-stage';
  hoverPreview.appendChild(previewHost);

  const submenu = document.createElement('div');
  submenu.className = 'menu-select__submenu';
  submenu.setAttribute('role', 'listbox');
  submenu.setAttribute('aria-hidden', 'true');

  item.append(trigger, hoverPreview, submenu);
  select.parentNode?.insertBefore(root, select);
  root.append(item, select);

  let hoverValue: string | null = null;

  function emitHover(value: string | null): void {
    if (hoverValue === value) return;
    hoverValue = value;
    const visible = value != null && value !== '';
    hoverPreview.classList.toggle('is-visible', visible);
    hoverPreview.setAttribute('aria-hidden', visible ? 'false' : 'true');
    options.onOptionHover?.({ value, previewHost });
  }

  function setOpen(open: boolean): void {
    root.classList.toggle('is-open', open);
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    submenu.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (!open) emitHover(null);
  }

  function syncDisabled(): void {
    trigger.disabled = select.disabled;
    root.classList.toggle('is-disabled', select.disabled);
  }

  function rebuildOptions(): void {
    submenu.replaceChildren();
    Array.from(select.options).forEach((opt) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'menu-select__option';
      btn.setAttribute('role', 'option');
      btn.dataset.value = opt.value;
      btn.textContent = opt.textContent ?? '';
      btn.disabled = opt.disabled;
      if (opt.selected) {
        btn.setAttribute('aria-selected', 'true');
        btn.classList.add('is-selected');
      } else {
        btn.setAttribute('aria-selected', 'false');
      }
      btn.addEventListener('pointerenter', () => {
        if (opt.disabled) {
          emitHover(null);
          return;
        }
        emitHover(opt.value || null);
      });
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (opt.disabled) return;
        const next = opt.value;
        if (select.value !== next) {
          select.value = next;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          labelEl.textContent = selectedLabel(select);
          rebuildOptions();
        }
        setOpen(false);
        trigger.focus();
      });
      submenu.appendChild(btn);
    });
    labelEl.textContent = selectedLabel(select);
  }

  submenu.addEventListener('pointerleave', () => {
    emitHover(null);
  });

  trigger.addEventListener('click', () => {
    if (select.disabled) return;
    setOpen(!root.classList.contains('is-open'));
  });

  trigger.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown' && !root.classList.contains('is-open')) {
      e.preventDefault();
      setOpen(true);
      const first = submenu.querySelector<HTMLButtonElement>('.menu-select__option:not(:disabled)');
      first?.focus();
    }
  });

  submenu.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      trigger.focus();
    }
  });

  const onDocPointer = (e: Event): void => {
    if (!root.classList.contains('is-open')) return;
    if (e.target instanceof Node && root.contains(e.target)) return;
    setOpen(false);
  };
  document.addEventListener('pointerdown', onDocPointer);

  select.addEventListener('change', () => {
    labelEl.textContent = selectedLabel(select);
    rebuildOptions();
  });
  select.addEventListener('menu-select:refresh', () => {
    rebuildOptions();
    syncDisabled();
  });

  const mo = new MutationObserver(() => {
    rebuildOptions();
    syncDisabled();
  });
  mo.observe(select, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['disabled'],
  });

  rebuildOptions();
  syncDisabled();

  return root;
}
